import { useEffect, useState, useRef } from "react";
import { Gamepad2, Monitor, Instagram, Pizza, Flame, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { fmtTime } from "@/lib/slots";

const ytEmbed = (u) => {
  if (!u) return "";
  const m = u.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&mute=1&loop=1&playlist=${m[1]}&controls=0` : "";
};

export default function DisplayPage() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const slidesRef = useRef([]);

  useEffect(() => {
    const load = () => api.get("/display/data").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, []);

  const build = (d) => {
    if (!d) return [];
    const s = d.slides || {};
    const out = [];
    if (s.now_playing && d.now_playing.length) out.push("now_playing");
    if (s.up_next && d.up_next.length) out.push("up_next");
    if (s.free_slots !== false && d.free_now && Object.keys(d.free_now).length) out.push("free_slots");
    if (s.free_bands !== false && d.free_bands && ((d.free_bands.today || []).some((x) => x.windows.length) || (d.free_bands.tomorrow || []).some((x) => x.windows.length))) out.push("free_bands");
    if (s.games && d.games.length) out.push("games");
    if (s.food && d.food.length) out.push("food");
    if (s.promos && d.promos.length) out.push("promos");
    if (s.announcements && d.announcements.length) out.push("announcements");
    if (s.instagram && d.instagram_handle) out.push("instagram");
    if (s.video && d.video_url) out.push("video");
    return out.length ? out : ["idle"];
  };

  useEffect(() => {
    slidesRef.current = build(data);
    const secs = (data?.rotation_seconds || 10) * 1000;
    const t = setInterval(() => setIdx((i) => (i + 1) % Math.max(1, slidesRef.current.length)), secs);
    return () => clearInterval(t);
  }, [data]);

  if (!data) return <div className="min-h-screen bg-[#05070f] flex items-center justify-center text-cyan-300 font-display text-2xl">Loading display…</div>;
  const slides = slidesRef.current.length ? slidesRef.current : build(data);
  const cur = slides[idx % slides.length];

  const Wrap = ({ children }) => (
    <div className="min-h-screen w-full bg-[#05070f] text-slate-100 overflow-hidden relative" style={{ fontFamily: "'Poppins','Inter',sans-serif" }} data-testid="display-page">
      <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 20% 20%, #1e1b4b 0, transparent 45%), radial-gradient(circle at 80% 80%, #0e7490 0, transparent 45%)" }} />
      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex items-center justify-between px-10 pt-7">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl border-2 border-cyan-400 flex items-center justify-center" style={{ boxShadow: "0 0 18px #22d3ee88" }}><Gamepad2 size={26} className="text-cyan-300" /></div>
            <div>
              <div className="text-2xl font-black tracking-wide" style={{ textShadow: "0 0 14px #22d3ee88" }}>PLAY STATION</div>
              <div className="text-xs tracking-[6px] text-cyan-300">GAMING CAFÉ</div>
            </div>
          </div>
          <div className="text-cyan-300 italic text-lg">Good Games · Better People</div>
        </div>
        <div className="flex-1 flex items-center justify-center px-12 py-8">{children}</div>
        <div className="text-center text-cyan-400/70 tracking-[4px] text-sm pb-6">PLAY • EAT • CONNECT</div>
      </div>
      <style>{`@keyframes fadein{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}} .fadein{animation:fadein .6s ease}`}</style>
    </div>
  );

  const Title = ({ icon: I, children, color = "#22d3ee" }) => (
    <div className="flex items-center gap-3 mb-8"><I size={40} style={{ color }} /><h1 className="text-5xl font-black" style={{ color, textShadow: `0 0 22px ${color}66` }}>{children}</h1></div>
  );

  const render = () => {
    if (cur === "now_playing") return (
      <div className="fadein w-full max-w-6xl">
        <Title icon={Flame} color="#f87171">🔴 LIVE NOW</Title>
        <div className="grid gap-5">
          {data.now_playing.slice(0, 4).map((n, i) => (
            <div key={i} className="rounded-3xl border border-red-500/40 bg-black/40 px-10 py-7 flex items-center justify-between" style={{ boxShadow: "0 0 30px #ef444422" }}>
              <div>
                <div className="text-4xl font-black">{n.station}</div>
                <div className="text-2xl text-cyan-200 mt-1">{n.setup}{n.customer ? ` · ${n.customer}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-mono font-bold text-red-300">{fmtTime(n.start)}–{fmtTime(n.end)}</div>
                <div className="text-lg text-slate-400">{n.remaining_min} min left</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
    if (cur === "free_slots") return (
      <div className="fadein w-full max-w-5xl text-center">
        <Title icon={Monitor} color="#4ade80">✅ AVAILABLE NOW</Title>
        <div className="grid grid-cols-2 gap-6">
          {Object.entries(data.free_now).map(([t, n]) => (
            <div key={t} className="rounded-3xl border border-emerald-500/40 bg-black/40 px-10 py-10" style={{ boxShadow: "0 0 30px #22c55e22" }}>
              <div className="text-3xl font-black uppercase text-emerald-300">{t}</div>
              <div className="text-7xl font-black mt-3">{n}</div>
              <div className="text-xl text-slate-300 mt-2">station{n === 1 ? "" : "s"} free</div>
            </div>
          ))}
        </div>
        <div className="text-2xl text-cyan-200 mt-8">Walk in or scan to book your slot now!</div>
      </div>
    );
    if (cur === "free_bands") return (
      <div className="fadein w-full max-w-6xl">
        <Title icon={CalendarClock} color="#4ade80">🕐 UPCOMING FREE SLOTS</Title>
        <div className="grid grid-cols-2 gap-8">
          {[["today", "TODAY"], ["tomorrow", "TOMORROW"]].map(([key, lbl]) => (
            <div key={key} className="rounded-3xl border border-emerald-500/40 bg-black/40 p-7" style={{ boxShadow: "0 0 30px #22c55e22" }}>
              <div className="text-3xl font-black text-emerald-300 mb-5">{lbl}</div>
              <div className="space-y-5">
                {(data.free_bands[key] || []).map((row) => (
                  <div key={row.type}>
                    <div className="text-2xl font-bold uppercase text-cyan-300 mb-2">{row.label}</div>
                    {row.windows.length ? (
                      <div className="flex flex-wrap gap-2">
                        {row.windows.map((w, i) => (
                          <span key={i} className="text-xl font-mono px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200">{fmtTime(w.start)}–{fmtTime(w.end)}</span>
                        ))}
                      </div>
                    ) : <span className="text-xl text-red-300">Fully booked</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="text-2xl text-cyan-200 mt-8 text-center">Come in during a free window — or scan to book ahead!</div>
      </div>
    );
    if (cur === "up_next") return (      <div className="fadein w-full max-w-6xl">
        <Title icon={CalendarClock}>UP NEXT</Title>
        <div className="grid grid-cols-2 gap-5">
          {data.up_next.slice(0, 6).map((n, i) => (
            <div key={i} className="rounded-2xl border border-cyan-500/30 bg-black/40 px-8 py-5">
              <div className="text-3xl font-bold">{n.station}</div>
              <div className="text-xl text-cyan-200">{n.setup}</div>
              <div className="text-2xl font-mono text-amber-300 mt-1">{fmtTime(n.start)}–{fmtTime(n.end)}</div>
            </div>
          ))}
        </div>
      </div>
    );
    if (cur === "games") return (
      <div className="fadein w-full max-w-6xl text-center">
        <Title icon={Flame} color="#a78bfa">🔥 WHAT'S HOT — PLAY TODAY</Title>
        <div className="grid grid-cols-4 gap-5">
          {data.games.slice(0, 8).map((g, i) => (
            <div key={i} className="rounded-2xl border border-purple-500/30 bg-black/40 overflow-hidden">
              {g.image ? <img src={g.image} alt={g.name} className="w-full h-40 object-cover" /> : <div className="w-full h-40 bg-purple-900/40 flex items-center justify-center"><Gamepad2 size={40} className="text-purple-300" /></div>}
              <div className="py-3 px-2"><div className="text-xl font-bold truncate">{g.name}</div><div className="text-xs uppercase text-cyan-300">{g.platform}</div></div>
            </div>
          ))}
        </div>
      </div>
    );
    if (cur === "food") return (
      <div className="fadein w-full max-w-6xl text-center">
        <Title icon={Pizza} color="#fbbf24">🍔 GAME • EAT • REPEAT</Title>
        <div className="grid grid-cols-4 gap-5">
          {data.food.slice(0, 8).map((f, i) => (
            <div key={i} className="rounded-2xl border border-amber-500/30 bg-black/40 px-4 py-6">
              <div className="text-2xl font-bold">{f.name}</div>
              <div className="text-3xl font-black text-amber-300 mt-2">₹{f.price}</div>
            </div>
          ))}
        </div>
        <div className="text-2xl text-cyan-200 mt-8">Order from your station!</div>
      </div>
    );
    if (cur === "promos" || cur === "announcements") {
      const list = cur === "promos" ? data.promos : data.announcements;
      const p = list[idx % list.length] || list[0];
      return (
        <div className="fadein text-center max-w-4xl">
          <div className="text-6xl font-black text-amber-300 mb-6" style={{ textShadow: "0 0 24px #fbbf2455" }}>{p.title}</div>
          <div className="text-3xl text-slate-200 leading-relaxed">{p.text}</div>
        </div>
      );
    }
    if (cur === "instagram") return (
      <div className="fadein text-center">
        <Title icon={Instagram} color="#e879f9">FOLLOW THE GRIND</Title>
        <div className="text-4xl font-bold text-fuchsia-300 mb-6">{data.instagram_handle}</div>
        <img alt="QR" className="mx-auto rounded-2xl bg-white p-3" src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(data.instagram_url)}`} />
        <div className="text-2xl text-cyan-200 mt-6">Scan · New Reels · Offers · Tournaments</div>
      </div>
    );
    if (cur === "video") {
      const yt = ytEmbed(data.video_url);
      return (
        <div className="fadein w-full max-w-5xl aspect-video rounded-3xl overflow-hidden border border-cyan-500/40">
          {yt ? <iframe title="promo" src={yt} className="w-full h-full" allow="autoplay; encrypted-media" frameBorder="0" />
              : <video src={data.video_url} className="w-full h-full object-cover" autoPlay muted loop playsInline />}
        </div>
      );
    }
    return (
      <div className="fadein text-center">
        <div className="text-6xl font-black neon" style={{ textShadow: "0 0 24px #22d3ee88" }}>PLAY STATION GAMING CAFÉ</div>
        <div className="text-3xl text-cyan-200 mt-6">Good Games · Better People</div>
      </div>
    );
  };

  return <Wrap>{render()}</Wrap>;
}
